import React from "react";

/**
 * Small helper: turn a hex color into an "accent + alpha" rgba-ish value
 * so we can tint borders/shadows/backgrounds without needing color-mix().
 */
function hexToRgba(hex, alpha) {
  let h = hex.replace("#", "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export default function StickyScroller({
  side = "left",
  eyebrow,
  title,
  description,
  accent = "#1a56db",
  items = [],
  renderBadge,
  maxWidth = "1280px",
}) {
  const accentSoft = hexToRgba(accent, 0.12);
  const accentGlow = hexToRgba(accent, 0.28);
  const accentLine = hexToRgba(accent, 0.35);

  const headingBlock = (
    <div
      className="flex-[0_0_42%] min-w-0 self-start flex flex-col justify-start px-4 md:px-8 md:sticky md:top-24"
      style={{ textAlign: side === "left" ? "left" : "right" }}
    >
      {eyebrow && (
        <div
          className="flex items-center gap-2 mb-5"
          style={{ justifyContent: side === "right" ? "flex-end" : "flex-start" }}
        >
          {side !== "right" && (
            <span
              className="h-1.5 w-1.5 rounded-full flex-shrink-0"
              style={{ background: accent, boxShadow: `0 0 0 4px ${accentSoft}` }}
            />
          )}
          <span
            className="text-[11px] font-semibold tracking-[0.28em] uppercase font-mono"
            style={{ color: accent }}
          >
            {eyebrow}
          </span>
          {side === "right" && (
            <span
              className="h-1.5 w-1.5 rounded-full flex-shrink-0"
              style={{ background: accent, boxShadow: `0 0 0 4px ${accentSoft}` }}
            />
          )}
        </div>
      )}

      <h2 className="text-5xl md:text-6xl font-extrabold text-slate-900 leading-[1.05] tracking-tight mb-5">
        {title}
      </h2>

      {description && (
        <p className="text-slate-500 text-lg md:text-xl leading-8 max-w-md" style={{ marginLeft: side === "right" ? "auto" : 0 }}>
          {description}
        </p>
      )}

      <div
        className="mt-9 h-[3px] w-20 rounded-full relative overflow-hidden"
        style={{ marginLeft: side === "right" ? "auto" : 0 }}
      >
        <div className="absolute inset-0" style={{ background: accent }} />
        <div
          className="absolute inset-0 opacity-70"
          style={{
            background: `linear-gradient(90deg, transparent, ${accentGlow}, transparent)`,
          }}
        />
      </div>
    </div>
  );

  const listBlock = (
    <div className="flex-[0_0_58%] min-w-0 relative px-4 md:px-8 pb-6">
      <div className="relative space-y-5">
        {/* connecting rail behind the badges */}
        {items.length > 1 && (
          <div
            aria-hidden="true"
            className="absolute top-6 bottom-6 w-px hidden sm:block"
            style={{
              left: "1.5rem",
              background: `linear-gradient(180deg, ${accentLine}, transparent 95%)`,
            }}
          />
        )}

        {items.map((item, i) => (
          <div
            key={i}
            className="group relative flex items-start gap-4 rounded-2xl p-5 border transition-all duration-300 ease-out hover:-translate-y-0.5"
            style={{
              background: "rgba(255,255,255,0.7)",
              backdropFilter: "blur(6px)",
              borderColor: "rgba(15, 23, 42, 0.06)",
              boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = accentGlow;
              e.currentTarget.style.boxShadow = `0 12px 28px -12px ${accentGlow}, 0 0 0 1px ${accentSoft}`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = "rgba(15, 23, 42, 0.06)";
              e.currentTarget.style.boxShadow = "0 1px 2px rgba(15,23,42,0.04)";
            }}
          >
            <div
              className="relative flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-sm z-10 transition-transform duration-300 group-hover:scale-105"
              style={{
                background: `linear-gradient(135deg, ${accent}, ${hexToRgba(accent, 0.75)})`,
                boxShadow: `0 0 0 4px ${accentSoft}, 0 6px 16px -6px ${accentGlow}`,
              }}
            >
              {renderBadge ? renderBadge(item, i) : String(i + 1).padStart(2, "0")}
            </div>

            <div className="min-w-0 pt-0.5">
              <h3 className="font-bold text-slate-800 text-[15px] mb-1.5 tracking-tight">
                {item.title}
              </h3>
              <p className="text-slate-500 text-sm leading-relaxed">{item.description}</p>
            </div>

            {/* subtle corner accent on hover */}
            <div
              className="absolute top-0 right-0 w-16 h-16 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
              style={{
                background: `radial-gradient(circle at top right, ${accentSoft}, transparent 70%)`,
              }}
            />
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <section className="relative bg-white py-24">
      {/* ambient background wash */}
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none"
        style={{
          background: `radial-gradient(ellipse 60% 50% at ${side === "left" ? "20%" : "80%"} 0%, ${hexToRgba(
            accent,
            0.06
          )}, transparent 70%)`,
        }}
      />

      <div className="mx-auto max-w-7xl px-4 sm:px-6 relative">
        <div
          className="grid gap-8 md:grid-cols-[42%_58%] items-start"
          style={{ maxWidth }}
        >
          {side === "left" ? (
            <>
              {headingBlock}
              {listBlock}
            </>
          ) : (
            <>
              {listBlock}
              {headingBlock}
            </>
          )}
        </div>
      </div>
    </section>
  );
}